import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n/LangContext";
import { translations } from "@/i18n/LangContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Ads conversion tracking — fires on successful inquiry submit.
// To enable proper conversion tracking, create a conversion action in
// Google Ads (Tools > Conversions > New) with category "Submit lead form",
// then replace the placeholder below with the value Google gives you:
//   `AW-18218553988/<your-conversion-label>`
// Until that's set, the event still fires against your account-level ID
// so Ads at minimum sees the conversion event happening.
const GOOGLE_ADS_CONVERSION_SEND_TO = "AW-18218553988/rvIWCKXJ8LwcEISlpO9D";

function translationsLookup(lang, path) {
  return path.split(".").reduce((acc, k) => acc?.[k], translations[lang]);
}

export default function Contact() {
  const { lang, t } = useLang();
  const projectTypes = t("contact.project_types");
  const productOptions = t("contact.product_options");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: projectTypes[0],
    product_interest: productOptions[0],
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  // When language changes, re-map the currently selected option (by index)
  // to the new locale's equivalent so the dropdown stays in sync.
  useEffect(() => {
    setForm((f) => {
      const otherLang = lang === "hu" ? "en" : "hu";
      const otherPT = translationsLookup(otherLang, "contact.project_types");
      const otherPO = translationsLookup(otherLang, "contact.product_options");
      const ptIdx = otherPT.indexOf(f.project_type);
      const poIdx = otherPO.indexOf(f.product_interest);
      return {
        ...f,
        project_type: ptIdx >= 0 ? projectTypes[ptIdx] : f.project_type,
        product_interest: poIdx >= 0 ? productOptions[poIdx] : f.product_interest,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Listen for prefill events dispatched from the cost estimator. We
  // populate the message field and pick the dropdown option that matches
  // the product the visitor came from. We prefer matching by the stable
  // `product_id` (e.g. "premium") via a fixed index map.
  const PRODUCT_ID_TO_OPTION_INDEX = {
    standard: 0,
    premium: 1,
  };
  useEffect(() => {
    const handler = (ev) => {
      const {
        message,
        product_interest,
        product_id,
        surfaces,
        total_area_m2,
        estimated_price_huf,
      } = ev.detail || {};
      setForm((f) => {
        const next = { ...f, message: message ?? f.message };
        if (product_id !== undefined) next.product_id = product_id;
        if (surfaces !== undefined) next.surfaces = surfaces;
        if (total_area_m2 !== undefined) next.total_area_m2 = total_area_m2;
        if (estimated_price_huf !== undefined) next.estimated_price_huf = estimated_price_huf;
        // 1) Preferred: pick the dropdown option by product_id index.
        const idx = PRODUCT_ID_TO_OPTION_INDEX[product_id];
        if (typeof idx === "number" && productOptions[idx]) {
          next.product_interest = productOptions[idx];
        } else if (product_interest) {
          // 2) Fallback: exact (case-insensitive) match by localized name.
          const target = String(product_interest).toLowerCase();
          const exact = productOptions.find((o) => o.toLowerCase() === target);
          if (exact) next.product_interest = exact;
        }
        return next;
      });
      setTimeout(() => {
        document.querySelector('[data-testid=input-name]')?.focus({ preventScroll: true });
      }, 600);
    };
    window.addEventListener("privateglass:prefill-inquiry", handler);
    return () => window.removeEventListener("privateglass:prefill-inquiry", handler);
  }, [productOptions]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", message: t("contact.error_required") });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      const { data } = await axios.post(`${API}/inquiries`, { ...form, locale: lang });

      // Google Ads conversion event — fires only on a confirmed 2xx response
      // from the backend so we don't count failed/abandoned submits. The
      // inquiry ID is passed as transaction_id so Google Ads can de-dup if
      // the user accidentally submits twice.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: GOOGLE_ADS_CONVERSION_SEND_TO,
          transaction_id: data?.id || "",
        });
      }

      setStatus({ state: "success", message: t("contact.success") });
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: projectTypes[0],
        product_interest: productOptions[0],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message: typeof detail === "string" ? detail : t("contact.error_generic"),
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="pt-10 md:pt-12 lg:pt-14 pb-24 md:pb-32 lg:pb-40 bg-[#F5F5F7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          data-testid="contact-divider"
          className="h-px bg-[#1D1D1F]/10 mb-14 md:mb-20"
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="text-[11px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
            {t("contact.overline")}
          </span>
          <h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl lg:text-6xl text-[#1D1D1F] leading-[1.05]">
            {t("contact.title_1")}{" "}
            <span className="font-extralight">{t("contact.title_2")}</span>
          </h2>
          <p className="mt-6 font-body text-base md:text-lg text-[#1D1D1F]/65 leading-relaxed max-w-md">
            {t("contact.intro")}
          </p>

          <div className="mt-12 space-y-6 max-w-sm">
            <div>
              <span className="text-[10px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {t("contact.info.email_label")}
              </span>
              <p className="mt-1 font-body text-[#1D1D1F]">info@privacyglass.hu</p>
            </div>
            <div>
              <span className="text-[10px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {t("contact.info.atelier_label")}
              </span>
              <p className="mt-1 font-body text-[#1D1D1F] whitespace-pre-line">
                {t("contact.info.atelier_value")}
              </p>
            </div>
            <div>
              <span className="text-[10px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {t("contact.info.hours_label")}
              </span>
              <p className="mt-1 font-body text-[#1D1D1F]">
                {t("contact.info.hours_value")}
              </p>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-white rounded-3xl border border-black/5 p-8 md:p-12 shadow-[0_18px_60px_rgba(0,0,0,0.04)]"
        >
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <Field label={t("contact.labels.name")} required>
              <input
                data-testid="input-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition"
                placeholder={t("contact.placeholders.name")}
              />
            </Field>
            <Field label={t("contact.labels.email")} required>
              <input
                data-testid="input-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition"
                placeholder={t("contact.placeholders.email")}
              />
            </Field>
            <Field label={t("contact.labels.phone")} required>
              <input
                data-testid="input-phone"
                type="tel"
                required
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                onFocus={(e) => {
                  const el = e.target;
                  if (!form.phone.trim()) {
                    update("phone", "+36 ");
                    // After React applies the new value, move the caret to
                    // the very end so the user types after the prefix even
                    // if they clicked before it.
                    requestAnimationFrame(() => {
                      const end = el.value.length;
                      try {
                        el.setSelectionRange(end, end);
                      } catch {
                        // type="tel" supports selection range; ignore if not.
                      }
                    });
                  } else {
                    // Field already has content — always park the caret at
                    // the end so the user appends rather than mid-string.
                    requestAnimationFrame(() => {
                      const end = el.value.length;
                      try {
                        el.setSelectionRange(end, end);
                      } catch {
                        // Some browsers don't allow setSelectionRange on type="tel" — ignore.
                      }
                    });
                  }
                }}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition"
                placeholder={t("contact.placeholders.phone")}
              />
            </Field>
            <Field label={t("contact.labels.project_type")}>
              <select
                data-testid="select-project-type"
                value={form.project_type}
                onChange={(e) => update("project_type", e.target.value)}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition appearance-none"
              >
                {projectTypes.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("contact.labels.product_interest")} full>
              <select
                data-testid="select-product"
                value={form.product_interest}
                onChange={(e) => {
                  const v = e.target.value;
                  // Also sync product_id so the email + PDF reflect the
                  // visitor's manual override of the dropdown.
                  const lo = v.toLowerCase();
                  let pid = null;
                  if (lo.includes("premium")) pid = "premium";
                  else if (lo.includes("standard")) pid = "standard";
                  setForm((f) => ({ ...f, product_interest: v, product_id: pid }));
                }}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition appearance-none"
              >
                {productOptions.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("contact.labels.message")} full required>
              <textarea
                data-testid="input-message"
                data-lenis-prevent
                required
                rows={10}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="w-full bg-[#F5F5F7] rounded-xl px-4 py-3.5 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none focus:ring-2 focus:ring-[#1D1D1F]/15 transition resize-none overflow-y-auto overscroll-contain"
                placeholder={t("contact.placeholders.message")}
              />
            </Field>
          </div>

          {status.state === "error" && (
            <div
              data-testid="contact-error"
              className="mt-6 text-sm font-body text-red-600"
            >
              {status.message}
            </div>
          )}
          {status.state === "success" && (
            <div
              data-testid="contact-success"
              className="mt-6 text-sm font-body text-[#1D1D1F] bg-[#34C759]/10 border border-[#34C759]/30 px-4 py-3 rounded-xl"
            >
              {status.message}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs font-body text-[#86868B] max-w-sm leading-relaxed">
              {t("contact.consent")}
            </p>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={status.state === "loading"}
              className="bg-[#1D1D1F] hover:bg-black disabled:opacity-50 text-white text-sm font-body font-medium rounded-full px-8 py-3.5 transition-colors"
            >
              {status.state === "loading"
                ? t("contact.submitting")
                : t("contact.submit")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`${full ? "md:col-span-2" : ""} block`}>
      <span className="text-[10px] font-body font-semibold tracking-[0.24em] uppercase text-[#86868B]">
        {label}
        {required && <span className="text-[#1D1D1F] ml-1">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
